// The rules both apps apply, as pure functions (docs/SHARED.md): web/rules.js ported to TypeScript with types only,
// behaviour frozen by shared/cases/*.json (tests/unit/rules.test.ts here, SharedCasesTests on iOS). Keep the two in
// step until the old site is retired (docs/ARCHITECTURE.md §3 slice 6).
import { SHARED, type Shared } from "./config";

/** An event as the apps hold it: the server's fields, with `start` / `end` parsed. */
export interface NowishEvent {
  id: string;
  title: string;
  start: Date;
  end: Date | null;
  end_inferred?: boolean | null;
  lat: number;
  lng: number;
  tags: string[];
  primary_tag?: string | null;
  emoji?: string | null;
  is_free?: boolean | null;
  price_min?: number | null;
  price_max?: number | null;
  price_advance?: number | null;
  price_door?: number | null;
  openness?: "open" | "drop_in_paid" | "ticketed" | null;
  needs_ticket?: boolean | null;
}

export type Level = "interest" | "none";
export type Tone = "now" | "soon" | "plain";
export type Shape = "squircle" | "ticket";
export interface Look { outline: Tone; shape: Shape; price: string | null }
export interface LatLng { lat: number; lng: number }
/** [west, south, east, north]; west > east crosses the antimeridian. */
export type Box = readonly [number, number, number, number];
export interface Window { start: Date; end: Date }
export interface GroupMember { emoji: string; level: Level; start: Date }
export interface TypeButton { label: "filter.all" | "filter.anyType" | null; icon: "star" | null; lead: string | null; emojis: string[] }
export interface FreePrice { freeOnly: boolean; maxPrice: number | null }
export interface HideSets { hiddenEvents: Set<string>; hiddenTags: Set<string>; interests: Set<string>; starredEvents: Set<string> }
type Levels = Map<string, string> | Record<string, string>;

export function makeRules(cfg: Shared) {
  const norm = (t: string) => String(t).trim().toLowerCase();
  const broad = new Set(cfg.broadInterests);
  const HOUR = 3600e3, UNKNOWN_END = cfg.unknownEnd;
  const tabById = (tabId: string) => {
    const tab = cfg.tabs.find((t) => t.id === tabId);
    if (!tab) throw new Error(`no tab "${tabId}" in nowish.json`);
    return tab;
  };

  // ---------- time ----------
  // Today ends at the next `endOfDayHour` (3am): before 3am it's still last night (ios NowishDay.endOfToday).
  function endOfToday(now = new Date()) {
    const d = new Date(now); d.setHours(cfg.endOfDayHour, 0, 0, 0);
    if (d <= now) d.setDate(d.getDate() + 1);
    return d;
  }
  // Nobody knows when it ends (the server's `end` is null; an end it inferred from clues counts as known): it lasts
  // unknownEnd.hours, its pin fading after the first few (pinOpacity), and the card says so (shared/cases/end-fade.json;
  // ios Event.endUnknown / effectiveEnd).
  const endUnknown = (e: NowishEvent) => !e.end;
  const effectiveEnd = (e: NowishEvent) => e.end || new Date(e.start.getTime() + UNKNOWN_END.hours * HOUR);
  const hasEnded = (e: NowishEvent, now: Date) => effectiveEnd(e) <= now;
  // A pin's (and its off-screen pointer's) opacity: 1, except an unknown end fades linearly from fadeAfterHours to
  // minOpacity at hours, "ghosting out" (ios EventLook.pinOpacity).
  function pinOpacity(e: NowishEvent, now: Date) {
    if (!endUnknown(e)) return 1;
    const { hours, fadeAfterHours, minOpacity } = UNKNOWN_END;
    const t = ((+now - +e.start) / HOUR - fadeAfterHours) / (hours - fadeAfterHours);
    return t <= 0 ? 1 : t >= 1 ? minOpacity : 1 - (1 - minOpacity) * t;
  }

  // 3am at the start of the day (3am to 3am) holding `now`, shifted by `plusDays` (ios LogicalDay.start). Wall-clock,
  // so 2:30am on the night the clocks go back is still the night before.
  function dayStart(now: Date, plusDays = 0) {
    const d = new Date(now);
    if (d.getHours() < cfg.endOfDayHour) d.setDate(d.getDate() - 1);
    d.setDate(d.getDate() + plusDays); d.setHours(cfg.endOfDayHour, 0, 0, 0);
    return d;
  }
  // 1 = Monday … 7 = Sunday, for the day (3am to 3am) holding `now`.
  const weekday = (now: Date) => (dayStart(now).getDay() + 6) % 7 + 1;

  // A tab's { start, end } (nowish.json tabs; shared/cases/tabs.json; ios TimeFilter.window): the rest of today, a
  // stretch of the week (Weekdays, Weekend: from now while it's on, else the next one), or `days` ahead.
  function tabWindow(tabId: string, now: Date): Window {
    const tab = tabById(tabId);
    if (tab.days != null) { const end = new Date(now); end.setDate(end.getDate() + tab.days); return { start: new Date(now), end }; }
    if (!tab.week) return { start: new Date(now), end: endOfToday(now) };
    const { from, to, currentUntil } = tab.week, d = weekday(now);
    if (d >= from && d <= currentUntil) return { start: new Date(now), end: dayStart(now, to - d + 1) };
    const ahead = d < from ? from - d : 7 - d + from;
    return { start: dayStart(now, ahead), end: dayStart(now, ahead + to - from + 1) };
  }
  const tabWindowEnd = (tabId: string, now: Date) => tabWindow(tabId, now).end;
  // The order the tabs show in today (nowish.json tabOrder).
  const tabOrder = (now: Date) => {
    const order = cfg.tabOrder.find((o) => o.weekdays.includes(weekday(now)));
    if (!order) throw new Error(`nowish.json tabOrder has no entry for weekday ${weekday(now)}`);
    return order.tabs;
  };
  // "Nothing here {phrase}": the tab's `emptyAhead` while its stretch starts later.
  function tabEmpty(tabId: string, now: Date) {
    const tab = tabById(tabId);
    return tab.emptyAhead && tabWindow(tabId, now).start > now ? tab.emptyAhead : tab.empty;
  }
  // How far ahead any tab reaches (what to fetch).
  const tabsReach = (now: Date) => new Date(Math.max(...cfg.tabs.map((t) => +tabWindowEnd(t.id, now))));
  function inTab(e: NowishEvent, tabId: string, now: Date) {
    if (hasEnded(e, now)) return false;
    const w = tabWindow(tabId, now);
    return e.start < w.end && effectiveEnd(e) > w.start;
  }

  // ---------- emoji and pin size ----------
  // The emoji an event shows everywhere (pins, groups, pointers, lists, card): its primary type's, never an
  // interest's instead (docs/SPEC.md item 4; shared/cases/pin-emoji.json). The server's emoji wins, else the first tag
  // with an emoji, primary tag first (ios Event.displayEmoji).
  function primaryEmoji(e: NowishEvent, tagEmoji: (tag: string) => string | null | undefined) {
    if (e.emoji && e.emoji !== cfg.fallbackEmoji) return e.emoji;
    for (const t of [e.primary_tag, ...e.tags].filter((x): x is string => !!x)) { const x = tagEmoji(t); if (x) return x; }
    return cfg.fallbackEmoji;
  }
  // "interest" | "none": sets the pin's size, cfg.pins.size[level] (ios InterestLevel.of).
  function interestLevel(e: NowishEvent, interests: Iterable<string>): Level {
    const i = new Set([...interests].map(norm));
    return e.tags.map(norm).some((t) => i.has(t)) ? "interest" : "none";
  }
  const pinSize = (level: Level) => cfg.pins.size[level];

  // A group's emojis: interest events first, then soonest; each once (ios GroupFace).
  const LEVEL_RANK: Record<Level, number> = { interest: 0, none: 1 };
  function groupEmojis(members: readonly GroupMember[]) {
    const seen: string[] = [];
    for (const m of [...members].sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level] || +a.start - +b.start)) {
      if (!seen.includes(m.emoji)) seen.push(m.emoji);
    }
    return seen.slice(0, cfg.group.maxEmojis);
  }

  // Only pins that pass the tab, filters and search group (not the selected / fallback pin shown outside them,
  // faded later search matches, or ticketed pins that thin out) (ios GroupFace.joins; shared/cases/group_pin.json).
  const joinsGroup = (pin: { passesFilters?: boolean; thinsOut?: boolean }) => !!pin.passesFilters && !pin.thinsOut;
  // A group pin takes its most favoured member's look, like its emojis: interest-sized if any member matches an
  // interest (group.scale × that pin size); its outline and shape come from pinStyle (ios GroupFace).
  const groupLevel = (levels: readonly Level[]): Level => (levels.includes("interest") ? "interest" : "none");
  const groupSize = (level: Level) => cfg.pins.size[level] * cfg.group.scale;
  function groupPin(members: readonly GroupMember[]) {
    const level = groupLevel(members.map((m) => m.level));
    return { emojis: groupEmojis(members), count: members.length, level, size: groupSize(level) };
  }

  // ---------- time tone: green now, blue soon (docs/SPEC.md item 4; shared/cases/time-label.json) ----------
  const isHappening = (e: NowishEvent, now: Date) => e.start <= now && now < effectiveEnd(e);
  // "now" (happening), "soon" (starting within pins.soonMinutes) or "plain" (later, or ended): the pin's outline and
  // the colour of its time text (ios EventFormat.timeTone).
  function timeTone(e: NowishEvent, now: Date): Tone {
    if (hasEnded(e, now)) return "plain";
    if (isHappening(e, now)) return "now";
    return +e.start - +now <= cfg.pins.soonMinutes * 60e3 ? "soon" : "plain";
  }
  // A group: "now" if any member is, else "soon" if any is.
  const groupTone = (events: readonly NowishEvent[], now: Date): Tone => {
    const tones = events.map((e) => timeTone(e, now));
    return tones.includes("now") ? "now" : tones.includes("soon") ? "soon" : "plain";
  };

  // "8pm", "7:30pm".
  function timeString(d: Date) {
    const h = d.getHours() % 12 || 12, m = d.getMinutes(), ap = d.getHours() < 12 ? "am" : "pm";
    return m === 0 ? `${h}${ap}` : `${h}:${String(m).padStart(2, "0")}${ap}`;
  }
  const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
  const dayMonth = (d: Date) => `${WEEKDAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}`;
  // "today", "tomorrow", a weekday within 6 days, else "Sat Nov 7" (days run 3am to 3am).
  function dayName(d: Date, now: Date): string {
    if (d >= dayStart(now) && d < dayStart(now, 1)) return "today";
    if (d >= dayStart(now, 1) && d < dayStart(now, 2)) return "tomorrow";
    return d < new Date(+now + 6 * 86400e3) ? WEEKDAYS[d.getDay()]! : dayMonth(d);
  }
  // The time text, everywhere an event's time shows (cards, list rows, search results, My events), with its colour:
  // { text, tone }. Now: "Now · until 9pm" ("until ~9pm" for an end inferred from clues; just "Now" when the end is
  // unknown) (green); soon: a countdown, "in 1 hr 45 min", "in 26 min" (blue); later:
  // "at 8pm" today, else "tomorrow 8pm", "Sat 2pm" (normal) (ios EventFormat.relative / timeTone).
  function timeLabel(e: NowishEvent, now: Date): { text: string; tone: Tone } {
    const tone = timeTone(e, now);
    if (hasEnded(e, now)) return { text: "Ended", tone };
    if (tone === "now") {
      if (!e.end) return { text: "Now", tone };
      const end = e.end;
      const endText = +dayStart(end) === +dayStart(now) || +end - +now < 8 * 3600e3 ? timeString(end) : `${dayName(end, now)} ${timeString(end)}`;
      return { text: e.end_inferred ? `Now · until ~${endText}` : `Now · until ${endText}`, tone };
    }
    if (tone === "soon") {
      const minutes = Math.max(1, Math.floor((+e.start - +now) / 60e3)), h = Math.floor(minutes / 60), m = minutes % 60;
      return { text: `in ${[h ? `${h} hr` : "", m ? `${m} min` : ""].filter(Boolean).join(" ")}`, tone };
    }
    const day = dayName(e.start, now);
    return { text: `${day === "today" ? "at" : day} ${timeString(e.start)}`, tone };
  }

  // ---------- how a pin looks (shared/cases/pin-style.json) ----------
  // Outline and shape: the time tone ("now" light green fill and green border, "soon" light blue and blue, "plain"
  // white); a plain squircle when free, a ticket-notched one otherwise; price: the corner label's text (pinPrice) or
  // null, never on a group (ios PinLook.face).
  const pinFace = (tone: Tone, free: boolean, group = false, price: string | null = null): Look =>
    ({ outline: tone, shape: free ? "squircle" : "ticket", price: group ? null : price });
  // { outline, shape, price } for one event (pins, pointers, list rows), or (an array) a group's members: their most
  // favoured look (green if any is now, else blue if any is soon; a plain squircle if any is free, ticket-notched only
  // when all are paid). The price label only when asked for (withPrice: the selected pin and list rows' icons), so
  // ordinary map pins keep one image per look. Nothing fades with time.
  function pinStyle(events: NowishEvent | readonly NowishEvent[], now: Date, { withPrice = false } = {}): Look {
    if (isEventList(events)) return pinFace(groupTone(events, now), events.some(isFreeForPin), true);
    return pinFace(timeTone(events, now), isFreeForPin(events), false, withPrice ? pinPrice(events) : null);
  }

  // The colour token for an off-screen pointer's chevron, from its pin's outline (pins.pointer.chevronColor: green
  // now, blue soon, grey later) (ios PinLook.pointerChevron).
  const pointerChevron = (outline: Tone) => cfg.pins.pointer.chevronColor[outline];

  // ---------- the Types filter (shared/cases/type-filter.json) ----------
  const LEVEL_ORDER: Record<string, number> = { interest: 0, none: 1, hidden: 2 };
  // tags: [{name, count}]; levels: Map / object tag → "interest" | "hidden".
  function typeOrder(tags: readonly { name: string; count: number }[], levels: Levels) {
    const lv = (t: { name: string }) =>
      (levels instanceof Map ? levels.get(norm(t.name)) : levels[norm(t.name)]) || "none";
    return [...tags].sort((a, b) => LEVEL_ORDER[lv(a)]! - LEVEL_ORDER[lv(b)]! || b.count - a.count).map((t) => t.name);
  }
  // { label } for "Types" / "All interests", else { emojis, more }: the selected types in list order.
  function typeButton(ordered: readonly string[], selected: Set<string>, allInterests: boolean,
    emojiOf: (tag: string) => string): TypeButton {
    // lead: the icon before the label (nowish.json anyTypeIcon, only on "Types"); icon: the one after it ("All ★").
    if (allInterests) return { label: "filter.all", icon: "star", lead: null, emojis: [] };
    const picked = ordered.filter((t) => selected.has(norm(t)));
    if (!picked.length) return { label: "filter.anyType", icon: null, lead: cfg.anyTypeIcon, emojis: [] };
    // Every one; the button shows as many as fit its width, then "…".
    return { label: null, icon: null, lead: null, emojis: picked.map(emojiOf) };
  }
  // Free and a set price exclude each other (shared/cases/free-price.json; ios EventFilter.setFree / setMaxPrice):
  // turning Free on resets the price to Any (null); setting a price turns Free off. A change is { freeOnly } or
  // { maxPrice }; returns the new { freeOnly, maxPrice }.
  function freePrice({ freeOnly, maxPrice }: FreePrice, change: { freeOnly: boolean } | { maxPrice: number | null }): FreePrice {
    if ("freeOnly" in change) return { freeOnly: change.freeOnly, maxPrice: change.freeOnly ? null : maxPrice };
    return { freeOnly: change.maxPrice != null ? false : freeOnly, maxPrice: change.maxPrice };
  }
  function matchesTypes(e: NowishEvent, selected: Set<string>, allInterests: boolean, interests: Set<string>) {
    const tags = e.tags.map(norm);
    if (allInterests) return tags.some((t) => interests.has(t));
    return !selected.size || tags.some((t) => selected.has(t));
  }

  // ---------- place ----------
  function metersBetween(a: LatLng, b: LatLng) {
    const R = 6371e3, rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  // The list of what's on the map, by time in the time tone's groups so it matches the colours
  // (shared/cases/list-order.json; ios Array.nowThenSoonest): happening now first, nearest first from `origin` (a tie
  // to the sooner start); then starting soon, then later, each soonest start first (a tie to the nearer one); ended last.
  const LIST_GROUP: Record<Tone, number> = { now: 0, soon: 1, plain: 2 };
  function nowThenSoonest<E extends NowishEvent>(events: readonly E[], origin: LatLng, now: Date): E[] {
    return events
      .map((e) => [e, hasEnded(e, now) ? 3 : LIST_GROUP[timeTone(e, now)], metersBetween(origin, e)] as const)
      .sort((a, b) => a[1] - b[1] || (a[1] === 0 ? a[2] - b[2] || +a[0].start - +b[0].start : +a[0].start - +b[0].start || a[2] - b[2]))
      .map((x) => x[0]);
  }

  // ---------- price ----------
  const priceUnknown = (e: NowishEvent) => e.is_free !== true && e.price_min == null && e.price_max == null;
  // Free (or $0) pins are plain squircles, without ticket notches (ios Event.isFreeForPin).
  const isFreeForPin = (e: NowishEvent) => e.is_free === true || (e.price_min === 0 && (e.price_max ?? 0) === 0);
  // The Free filter keeps exactly the plain pins: one definition of free (ios EventFilter.freeOnly).
  const passesFreeFilter = (e: NowishEvent) => isFreeForPin(e);

  // Price labels (shared/cases/price_label.json, ios EventFormat). Unknown openness reads like drop_in_paid.
  const openness = (e: NowishEvent) => (e.needs_ticket === true ? "ticketed" : e.openness || "drop_in_paid");
  // How you get in: "Walk up, free", "Walk up", "Pay at the door", "Ticket needed".
  function access(e: NowishEvent) {
    switch (openness(e)) {
      case "open": return e.is_free === false ? "Walk up" : "Walk up, free";
      case "drop_in_paid": return e.is_free === true ? "Walk up, free" : "Pay at the door";
      default: return "Ticket needed";
    }
  }
  const dollars = (v: number) => (Math.round(v) === v ? `$${v}` : `$${v.toFixed(2)}`);
  // An advance and a different door price both known: "$20 advance · $25 at the door".
  const advanceAndDoor = (e: NowishEvent): e is NowishEvent & { price_advance: number; price_door: number } =>
    e.is_free !== true && e.price_advance != null && e.price_door != null && e.price_advance !== e.price_door;
  // "Free", "$10", "$10–$25", "$20 advance · $25 at the door", "Paid · price unknown", "Price unknown".
  function price(e: NowishEvent) {
    if (e.is_free === true) return "Free";
    if (advanceAndDoor(e)) return `${dollars(e.price_advance)} advance · ${dollars(e.price_door)} at the door`;
    const min = e.price_min, max = e.price_max;
    if (min != null && max != null && max > min) return `${dollars(min)}–${dollars(max)}`;
    if (min != null) return min === 0 ? "Free" : dollars(min);
    if (max != null) return `Up to ${dollars(max)}`;
    return e.is_free === false ? "Paid · price unknown" : "Price unknown";
  }
  // The price on its own for list rows: "Free", "$10", "$10–$25", "Paid"; null when unknown.
  function shortPrice(e: NowishEvent) {
    if (advanceAndDoor(e)) return `${dollars(Math.min(e.price_advance, e.price_door))}–${dollars(Math.max(e.price_advance, e.price_door))}`;
    const p = price(e);
    return p === "Price unknown" ? null : p === "Paid · price unknown" ? "Paid" : p;
  }
  // A paid pin's corner label (docs/SPEC.md item 4): the lowest known price above $0 in whole dollars, "$1k+" from
  // $1,000; null when free or no amount is known (ios EventFormat.pinPrice).
  function pinPrice(e: NowishEvent) {
    if (isFreeForPin(e)) return null;
    const known = [e.price_min, e.price_advance, e.price_door, e.price_max].filter((v): v is number => v != null && v > 0);
    if (!known.length) return null;
    const v = Math.min(...known);
    return v >= 1000 ? `$${Math.floor(v / 1000)}k+` : `$${Math.round(v)}`;
  }
  // How you get in and what it costs, as one line that never contradicts itself:
  // "Walk up, free", "Walk up · $5", "Pay at the door · $10", "Ticket needed · price unknown", "$20 advance · $25 at the door".
  function admission(e: NowishEvent) {
    const a = access(e), p = price(e);
    if (advanceAndDoor(e)) return p;
    if (p === "Price unknown") return a === "Ticket needed" ? "Ticket needed · price unknown" : a;
    if (p === "Paid · price unknown") return a === "Walk up" ? "Walk up · paid" : a;
    if (p === "Free") return a.includes("free") ? a : `${a} · free`;
    return a === "Walk up, free" ? `Walk up · ${p}` : `${a} · ${p}`;
  }

  // ---------- fetching (nowish.json fetch; shared/cases/fetch-area.json; ios FetchArea) ----------
  const wrapLng = (x: number) => (x >= -180 && x <= 180 ? x : ((((x + 180) % 360) + 360) % 360) - 180);
  const lngSpan = ([w, , e]: Box) => (e - w >= 360 ? 360 : e >= w ? e - w : e - w + 360);
  // The area to fetch for a view: grown by marginFactor of its size on every side, at least minSpanDegrees.
  function fetchArea(view: Box): Box {
    const f = cfg.fetch, [w, s, , n] = view, width = lngSpan(view), height = n - s;
    const padX = (Math.max(width * (1 + 2 * f.marginFactor), f.minSpanDegrees) - width) / 2;
    const padY = (Math.max(height * (1 + 2 * f.marginFactor), f.minSpanDegrees) - height) / 2;
    const south = Math.max(-90, s - padY), north = Math.min(90, n + padY);
    if (width + 2 * padX >= 360) return [-180, south, 180, north];
    return [wrapLng(w - padX), south, wrapLng(w + width + padX), north];
  }
  // Whether a fetched area holds all of a view.
  function covers(area: Box, view: Box) {
    if (view[1] < area[1] || view[3] > area[3]) return false;
    const aw = lngSpan(area), vw = lngSpan(view);
    if (aw >= 360) return true;
    const offset = ((((view[0] - area[0]) % 360) + 360) % 360);
    return offset + vw <= aw + 1e-9;
  }

  // ---------- hiding ----------
  // Hidden when you hid it, or it has a hidden tag and none of your interests (an interest wins; broad ones
  // don't count). A starred event never hides (ios HiddenSet.hides).
  function hides(e: NowishEvent, { hiddenEvents, hiddenTags, interests, starredEvents }: HideSets) {
    if (starredEvents.has(e.id)) return false;
    if (hiddenEvents.has(e.id)) return true;
    if (!hiddenTags.size) return false;
    const tags = e.tags.map(norm);
    return tags.some((t) => hiddenTags.has(t)) && !tags.some((t) => interests.has(t) && !broad.has(t));
  }

  return { norm, endOfToday, endUnknown, effectiveEnd, hasEnded, pinOpacity, tabWindow, tabWindowEnd, tabOrder, tabEmpty, tabsReach, inTab, primaryEmoji, interestLevel, pinSize,
    groupEmojis, joinsGroup, groupPin, groupLevel, groupSize, isHappening, timeTone, groupTone, timeString, dayMonth, dayName, timeLabel, pinFace, pinStyle, pointerChevron, typeOrder, typeButton, freePrice, matchesTypes, metersBetween, nowThenSoonest, priceUnknown, isFreeForPin, passesFreeFilter,
    openness, access, dollars, price, shortPrice, pinPrice, admission, hides, fetchArea, covers };
}

function isEventList(x: NowishEvent | readonly NowishEvent[]): x is readonly NowishEvent[] {
  return Array.isArray(x);
}

export type Rules = ReturnType<typeof makeRules>;
export const RULES: Rules = makeRules(SHARED);
